/// <reference lib="dom" />

import { getAccessToken } from '../auth-token'
import type {
  SchedulePeriod,
  ClassSectionWithRelations,
  Subject,
  AbsenceType,
  EngineResult,
} from '@timetable/types'

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

// ============================================================================
// Types
// ============================================================================

export interface DiscoverPeriodsResult {
  periods: SchedulePeriodWithContext[]
  skipped_reason?: EngineResult['skipped_reason']
}

export interface SchedulePeriodWithContext {
  period: SchedulePeriod
  class_section_id: string
  subject_id: string
  class_section: ClassSectionWithRelations
  subject: Subject
}

// ============================================================================
// Helpers
// ============================================================================

const DAY_OF_WEEK_MAP: Record<number, string> = {
  0: 'SUNDAY',
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
}

function getDayOfWeek(isoDate: string): string {
  // Parse date as local date to avoid timezone shifts
  const [year, month, day] = isoDate.split('-').map(Number)
  const d = new Date(year, month - 1, day)
  return DAY_OF_WEEK_MAP[d.getDay()]
}

function authHeaders(accessToken: string): Record<string, string> {
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  }
}

// ============================================================================
// discoverPeriods
// ============================================================================

/**
 * Discover the periods an absent teacher is scheduled for on a given date.
 * Returns a DiscoverPeriodsResult with the periods and an optional skipped_reason.
 */
export async function discoverPeriods(
  absentTeacherId: string,
  targetDate: string,
  absenceType: AbsenceType,
  absenceStartTime: string | null | undefined,
  absenceEndTime: string | null | undefined,
  schoolId: string
): Promise<DiscoverPeriodsResult> {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Not authenticated')

  const headers = authHeaders(accessToken)

  // Step 1: Find the published academic year for the school
  const ayParams = new URLSearchParams()
  ayParams.append('select', 'id,start_date,end_date,status')
  ayParams.append('school_id', `eq.${schoolId}`)
  ayParams.append('status', `eq.published`)
  ayParams.append('limit', '1')

  const ayResponse = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?${ayParams}`, {
    headers,
  })

  if (!ayResponse.ok) {
    const err = await ayResponse.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to fetch academic years')
  }

  const academicYears: any[] = await ayResponse.json()
  if (!academicYears || academicYears.length === 0) {
    return { periods: [], skipped_reason: 'no_published_academic_year' }
  }

  const publishedYear = academicYears[0]

  // Step 2: Determine day of week from targetDate
  const dayOfWeek = getDayOfWeek(targetDate)

  // Step 3: Check for holiday or alternate_saturday_off override on targetDate
  const overrideParams = new URLSearchParams()
  overrideParams.append('select', 'id,override_type,alternate_schedule_id')
  overrideParams.append('school_id', `eq.${schoolId}`)
  overrideParams.append('override_date', `eq.${targetDate}`)
  overrideParams.append('override_type', `in.(holiday,alternate_saturday_off,half_day_schedule)`)

  const overrideResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/schedule_overrides?${overrideParams}`,
    { headers }
  )

  let halfDayOverride: any = null

  if (overrideResponse.ok) {
    const overrides: any[] = await overrideResponse.json()
    for (const override of overrides) {
      if (
        override.override_type === 'holiday' ||
        override.override_type === 'alternate_saturday_off'
      ) {
        return { periods: [], skipped_reason: 'non_working_day' }
      }
      if (override.override_type === 'half_day_schedule') {
        halfDayOverride = override
      }
    }
  }

  // Step 4: Query timetable_records for the absent teacher on this day
  const ttParams = new URLSearchParams()
  ttParams.append(
    'select',
    [
      'id',
      'class_section_id',
      'subject_id',
      'period_id',
      'class_section:class_sections(*, class:classes(*, class_schedule:class_schedules(*)))',
      'subject:subjects(*)',
      'period:schedule_periods(*)',
    ].join(',')
  )
  ttParams.append('teacher_id', `eq.${absentTeacherId}`)
  ttParams.append('day_of_week', `eq.${dayOfWeek}`)
  ttParams.append('academic_year_id', `eq.${publishedYear.id}`)

  const ttResponse = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?${ttParams}`, {
    headers,
  })

  if (!ttResponse.ok) {
    const err = await ttResponse.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to fetch timetable records')
  }

  let records: any[] = await ttResponse.json()

  // Step 5: Handle half_day_schedule override — replace periods with alternate schedule periods
  if (halfDayOverride && halfDayOverride.alternate_schedule_id) {
    const altScheduleId = halfDayOverride.alternate_schedule_id

    // Fetch periods from the alternate schedule
    const altParams = new URLSearchParams()
    altParams.append('select', '*')
    altParams.append('class_schedule_id', `eq.${altScheduleId}`)

    const altResponse = await fetch(`${SUPABASE_URL}/rest/v1/schedule_periods?${altParams}`, {
      headers,
    })

    if (altResponse.ok) {
      const altPeriods: SchedulePeriod[] = await altResponse.json()
      const altPeriodsByNumber = new Map<number, SchedulePeriod>()
      for (const p of altPeriods) {
        altPeriodsByNumber.set(p.period_number, p)
      }

      // Replace each record's period with the matching alternate period (by period_number)
      records = records
        .map((record) => {
          const originalPeriodNumber = record.period?.period_number
          if (originalPeriodNumber == null) return record
          const altPeriod = altPeriodsByNumber.get(originalPeriodNumber)
          if (!altPeriod) return null // period doesn't exist in half-day schedule — skip
          return { ...record, period: altPeriod, period_id: altPeriod.id }
        })
        .filter(Boolean)
    }
  }

  // Step 6: Filter for temporary_duty time overlap
  if (absenceType === 'temporary_duty' && absenceStartTime && absenceEndTime) {
    records = records.filter((record) => {
      const period = record.period
      if (!period) return false
      // period.start_time < absenceEndTime AND period.end_time > absenceStartTime
      return period.start_time < absenceEndTime && period.end_time > absenceStartTime
    })
  }

  // Step 7: If no records, return no_scheduled_periods
  if (!records || records.length === 0) {
    return { periods: [], skipped_reason: 'no_scheduled_periods' }
  }

  // Step 8: Map to SchedulePeriodWithContext[]
  const periods: SchedulePeriodWithContext[] = records.map((record) => ({
    period: record.period as SchedulePeriod,
    class_section_id: record.class_section_id as string,
    subject_id: record.subject_id as string,
    class_section: record.class_section as ClassSectionWithRelations,
    subject: record.subject as Subject,
  }))

  return { periods }
}
